const API_URL = process.env.REACT_APP_API_URL;

// Fetch clients (paginated or search)
export async function getClients(page = 1, limit = 10, search = "") {
  let url = "";

  if (search && search.trim() !== "") {
    const formatted = search.toString().trim().replace(/'/g, "''");
    url = `${API_URL}/client?requete=nom:'${formatted}' OR telephone:'${formatted}'&page=${page}&limite=${limit}`;
  } else {
    url = `${API_URL}/client?page=${page}&limite=${limit}`;
  }

  const response = await fetch(url, {
    method: "GET",
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Non authentifié");
  }

  return response.json();
  }

// Fetch client by ID
export async function getClientById(clientId) {
  const response = await fetch(`${API_URL}/client/${clientId}`, {
    method: "GET",
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Client non trouvé");
  }

  return response.json();
}

// Fetch contracts for a client
export async function getContratsByClientId(clientId) {
  const response = await fetch(`${API_URL}/client/${clientId}/contrat`, {
    method: "GET",
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Erreur lors de la récupération des contrats");
  }
  return response.json();
}

export async function getContractTypes() {
  const response = await fetch(`${API_URL}/contrat/types`, {
    method: "GET",
    credentials: "include",
  });  

  if (!response.ok) {
    throw new Error("Erreur lors de la récupération des types de contrat");
  }

  return response.json();
}

export async function createContract(clientId, contractData) {
  const response = await fetch(`${API_URL}/client/${clientId}/contrat`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(contractData),
  });

  if (!response.ok) {
    let errorMessage = "Erreur lors de la création du contrat";
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.detail || errorData.error || errorMessage;
    } catch {}
    throw new Error(errorMessage);
  }

  return response.json();
}

export async function updateContract(clientId, contractId, contractData) {
  const response = await fetch(`${API_URL}/client/${clientId}/contrat/${contractId}`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(contractData),
  });

  if (!response.ok) {
    let errorMessage = "Erreur lors de la mise à jour du contrat";
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.detail || errorData.error || errorMessage;
    } catch {}
    throw new Error(errorMessage);
  }

  return response.json();
}

export async function deleteContract(clientId, contractId) {
  const response = await fetch(
    `${API_URL}/client/${clientId}/contrat/${contractId}`,
    {
      method: "DELETE",
      credentials: "include",
    }
  );

  if (response.status === 204 || response.ok) {
    try { return await response.json(); } catch { return {}; }
  }

  let errorMessage = "Erreur lors de la suppression du contrat";
  try {
    const errorData = await response.json();
    errorMessage = errorData.message || errorData.detail || errorData.error || errorMessage;
  } catch {}

  throw new Error(errorMessage);
}

export async function getContactByClientId(clientId) {
  const response = await fetch(`${API_URL}/client/${clientId}/contact`, {
    method: "GET",
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Non authentifié");
  }

  return response.json();
}

export async function getTicketsByClientId(clientId) {
  const response = await fetch(`${API_URL}/client/${clientId}/ticket`, {
    method: "GET",
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Non authentifié");
  }

  return response.json();
}

// GET custom fields associated to a client (filtered by all_clients or explicit link)
export async function getClientChamps(clientId) {
  const response = await fetch(`${API_URL}/client/${clientId}/champs`, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });

  if (!response.ok) {
    throw new Error("Erreur lors de la récupération des champs du client");
  }

  return response.json();
}

export async function getUrlsByClientId(clientId) {
  const response = await fetch(`${API_URL}/client/${clientId}/url`, {
    method: "GET",
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Non authentifié");
  }

  return response.json();
}

export async function createClientUrl(clientId, urlData) {
  const response = await fetch(`${API_URL}/client/${clientId}/url`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(urlData),
  });

  if (!response.ok) {
    throw new Error("Erreur lors de la création de l'URL");
  }

  return response.json();
}

export async function updateClientUrl(clientId, urlId, urlData) {
  const response = await fetch(`${API_URL}/client/${clientId}/url/${urlId}`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(urlData),
  });

  if (!response.ok) {
    throw new Error("Erreur lors de la modification de l'URL");
  }

  return response.json();
}

export async function deleteClientUrl(clientId, urlId) {
  const response = await fetch(`${API_URL}/client/${clientId}/url/${urlId}`, {
    method: "DELETE",
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Erreur lors de la suppression de l'URL");
  }

  if (response.status === 204) return {};
  return response.json();
}

export async function createClient(clientData) {
  const response = await fetch(`${API_URL}/client`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(clientData),
  });

  if (!response.ok) {
    throw new Error("Erreur lors de la création du client");
  }

  return response.json();
}

export async function updateClient(clientId, clientData) {
  const response = await fetch(`${API_URL}/client/${clientId}`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(clientData),
  });

  if (!response.ok) {
    let errorMessage = "Erreur lors de la mise à jour du client";
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.detail || errorData.error || errorMessage;
    } catch (e) {
      try {
        const errorText = await response.text();
        if (errorText) errorMessage = errorText;
      } catch {}
    }
    const error = new Error(errorMessage);
    error.response = { data: { message: errorMessage }, status: response.status };
    throw error;
  }

  return response.json();
}

export async function updateClientContact(clientId, contactId, contactData) {
  const response = await fetch(`${API_URL}/client/${clientId}/contact/${contactId}`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(contactData),
  });

  if (!response.ok) {
    let errorMessage = "Erreur lors de la mise à jour du contact";
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.detail || errorData.error || errorMessage;
    } catch {}
    const error = new Error(errorMessage);
    error.response = { data: { message: errorMessage }, status: response.status };
    throw error;
  }

  return response.json();
}

export async function createContact(clientId, contactData) {
  const response = await fetch(`${API_URL}/client/${clientId}/contact`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(contactData),
  });

  if (!response.ok) {
    let errorMessage = "Erreur lors de la création du contact";
    try {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.detail || errorData.error || errorMessage;
    } catch {}
    throw new Error(errorMessage);
  }

  return response.json();
}

export async function deleteClientContact(clientId, contactId) {
  const response = await fetch(`${API_URL}/client/${clientId}/contact/${contactId}`, {
    method: "DELETE",
    credentials: "include",
  });

  if (response.status === 204 || response.ok) {
    try { return await response.json(); } catch { return {}; }
  }

  let errorMessage = "Erreur lors de la suppression du contact";
  try {
    const errorData = await response.json();
    errorMessage = errorData.message || errorData.detail || errorData.error || errorMessage;
  } catch {}

  const error = new Error(errorMessage);
  error.response = { data: { message: errorMessage }, status: response.status };
  throw error;
}

export async function deleteClient(clientId) {
  const response = await fetch(`${API_URL}/client/${clientId}`, {
    method: "DELETE",
    credentials: "include",
  });

  if (response.status === 204 || response.ok) {
    try { return await response.json(); } catch { return {}; }
  }

  let errorMessage = "Erreur lors de la suppression du client";
  try {
    const errorData = await response.json();
    errorMessage = errorData.message || errorData.detail || errorData.error || errorMessage;
  } catch {}

  const error = new Error(errorMessage);
  error.response = { data: { message: errorMessage }, status: response.status };
  throw error;
}